import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Bell, Save, RefreshCw, CheckCircle, XCircle, Trash2, Plus } from "lucide-react";
import complianceApi from "../../api/complianceApi";
import accountApi from "../../api/accountApi";
import { toast } from "react-toastify";

const COMPLIANCE_CATEGORIES = [
  { value: "GST", label: "GST Compliance" },
  { value: "TDS", label: "TDS Compliance" },
  { value: "INCOME_TAX", label: "Income Tax" },
  { value: "LEGAL", label: "Legal Compliance" },
  { value: "INTERNAL_ACCOUNTING", label: "Internal Accounting" },
];

const STATUS_OPTIONS = [
  { key: "notifyOnPending", label: "Pending" },
  { key: "notifyOnVerified", label: "Verified" },
  { key: "notifyOnDisputed", label: "Disputed" },
  { key: "notifyOnBooked", label: "Booked" },
  { key: "notifyOnFiled", label: "Filed" },
];

const AccountNotificationSettings = () => {
  const token = useSelector((state) => state.auth.access_token);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [settings, setSettings] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (token) {
      fetchAccounts();
    }
  }, [token]);

  useEffect(() => {
    if (selectedAccount && token) {
      fetchAccountSettings();
    }
  }, [selectedAccount, token]);

  const fetchAccounts = async () => {
    try {
      const response = await accountApi.getAllAccounts(token);
      if (response && response.data && response.data.content) {
        setAccounts(response.data.content);
      }
    } catch (error) {
      console.error("Error fetching accounts:", error);
      toast.error("Failed to load accounts");
    }
  };

  const fetchAccountSettings = async () => {
    setLoading(true);
    try {
      const response = await complianceApi.getAccountNotificationSettings(selectedAccount.id, token);
      const settingsMap = {};

      if (response && response.data) {
        response.data.forEach((setting) => {
          settingsMap[setting.complianceCategory] = setting;
        });
      }

      // Initialize missing categories with defaults
      COMPLIANCE_CATEGORIES.forEach((cat) => {
        if (!settingsMap[cat.value]) {
          settingsMap[cat.value] = {
            accountId: selectedAccount.id,
            complianceCategory: cat.value,
            notifyOnPending: false,
            notifyOnVerified: true,
            notifyOnDisputed: true,
            notifyOnBooked: false,
            notifyOnFiled: true,
            isEnabled: true,
            recipientEmail: selectedAccount.email || "",
            ccEmails: "",
            remarks: "",
          };
        }
      });

      setSettings(settingsMap);
    } catch (error) {
      console.error("Error fetching account settings:", error);
      toast.error("Failed to load account notification settings");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (category, field) => {
    setSettings((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: !prev[category][field],
      },
    }));
  };

  const handleInputChange = (category, field, value) => {
    setSettings((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
  };

  const saveSettings = async () => {
    if (!selectedAccount) {
      toast.error("Please select an account");
      return;
    }

    setSaving(true);
    try {
      const promises = Object.values(settings).map((setting) =>
        complianceApi.saveAccountNotificationSettings(setting, token)
      );

      await Promise.all(promises);
      toast.success("Account notification settings saved successfully");
      fetchAccountSettings();
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save notification settings");
    } finally {
      setSaving(false);
    }
  };

  const filteredAccounts = accounts.filter((account) =>
    account.accountName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Bell className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-800">
                Account-Level Notification Settings
              </h3>
              <p className="text-sm text-slate-500">
                Configure email notifications for individual accounts
              </p>
            </div>
          </div>

          {selectedAccount && (
            <div className="flex gap-2">
              <button
                onClick={fetchAccountSettings}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-2"
              >
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                Refresh
              </button>
              <button
                onClick={saveSettings}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                ) : (
                  <Save size={16} />
                )}
                Save Settings
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 divide-x divide-slate-200">
        {/* Account List */}
        <div className="col-span-3 p-4 bg-slate-50">
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          <div className="space-y-1 max-h-[600px] overflow-y-auto">
            {filteredAccounts.map((account) => (
              <button
                key={account.id}
                onClick={() => setSelectedAccount(account)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedAccount?.id === account.id
                    ? "bg-blue-100 text-blue-800 font-medium"
                    : "hover:bg-slate-100 text-slate-700"
                }`}
              >
                <div className="font-medium">{account.accountName}</div>
                {account.email && (
                  <div className="text-xs text-slate-500 truncate">{account.email}</div>
                )}
              </button>
            ))}
            {filteredAccounts.length === 0 && (
              <div className="text-center py-8 text-sm text-slate-500">
                No accounts found
              </div>
            )}
          </div>
        </div>

        {/* Settings Panel */}
        <div className="col-span-9 p-6">
          {!selectedAccount ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Bell className="w-12 h-12 mb-4 text-slate-300" />
              <p className="text-lg font-medium">Select an account</p>
              <p className="text-sm">Choose an account from the list to configure notifications</p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Account Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-800">
                      {selectedAccount.accountName}
                    </h4>
                    <p className="text-sm text-slate-600">{selectedAccount.email}</p>
                  </div>
                  <div className="text-xs text-slate-500">
                    Account ID: {selectedAccount.id}
                  </div>
                </div>
              </div>

              {/* Category Settings */}
              {COMPLIANCE_CATEGORIES.map((category) => {
                const categorySetting = settings[category.value] || {};
                return (
                  <div
                    key={category.value}
                    className="border border-slate-200 rounded-lg overflow-hidden"
                  >
                    {/* Category Header */}
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                      <h4 className="font-semibold text-slate-800">{category.label}</h4>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-sm text-slate-600">Enable</span>
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={categorySetting.isEnabled || false}
                            onChange={() => handleToggle(category.value, "isEnabled")}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </div>
                      </label>
                    </div>

                    {/* Status Toggles */}
                    <div className="p-4">
                      <div className="grid grid-cols-5 gap-3 mb-4">
                        {STATUS_OPTIONS.map((status) => (
                          <div
                            key={status.key}
                            className={`p-3 rounded-lg border text-center ${
                              categorySetting[status.key]
                                ? "border-blue-300 bg-blue-50"
                                : "border-slate-200 bg-white"
                            }`}
                          >
                            <label className="cursor-pointer">
                              <input
                                type="checkbox"
                                checked={categorySetting[status.key] || false}
                                onChange={() => handleToggle(category.value, status.key)}
                                disabled={!categorySetting.isEnabled}
                                className="sr-only"
                              />
                              <div className="flex flex-col items-center gap-2">
                                {categorySetting[status.key] ? (
                                  <CheckCircle className="w-5 h-5 text-blue-600" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-slate-300" />
                                )}
                                <span className="text-xs font-medium text-slate-700">
                                  {status.label}
                                </span>
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>

                      {/* Email Settings */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Recipient Email
                          </label>
                          <input
                            type="email"
                            value={categorySetting.recipientEmail || ""}
                            onChange={(e) =>
                              handleInputChange(category.value, "recipientEmail", e.target.value)
                            }
                            disabled={!categorySetting.isEnabled}
                            placeholder="recipient@example.com"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-slate-50"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            CC Emails (comma-separated)
                          </label>
                          <input
                            type="text"
                            value={categorySetting.ccEmails || ""}
                            onChange={(e) =>
                              handleInputChange(category.value, "ccEmails", e.target.value)
                            }
                            disabled={!categorySetting.isEnabled}
                            placeholder="cc1@example.com, cc2@example.com"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-slate-50"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountNotificationSettings;
